import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_KEY = "elderra_assistant_session";
const POLL_MS = 8000;

function getSessionId() {
  let sid = localStorage.getItem(SESSION_KEY);
  if (!sid) {
    sid = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);
    localStorage.setItem(SESSION_KEY, sid);
  }
  return sid;
}

const mergeById = (prev, incoming) => {
  const known = new Set(prev.map((m) => m.id));
  const fresh = incoming.filter((m) => m && !known.has(m.id));
  return fresh.length ? [...prev, ...fresh] : prev;
};

/** Web chat on the shared Elderra assistant engine (same brain as WhatsApp). */
export function useAssistantChat() {
  const [sessionId] = useState(getSessionId);
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const started = useRef(false);

  const refresh = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/assistant/messages`, { params: { session_id: sessionId }, withCredentials: true });
      setMessages((prev) => mergeById(prev.filter((m) => !String(m.id).startsWith("temp-")), res.data.messages || []));
      return res.data.messages || [];
    } catch (e) {
      console.error("assistant refresh failed", e);
      return [];
    }
  }, [sessionId]);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    (async () => {
      const existing = await refresh();
      if (existing.length === 0) {
        try {
          const res = await axios.post(`${API}/assistant/start`, { session_id: sessionId }, { withCredentials: true });
          setMessages((prev) => mergeById(prev, res.data.messages || []));
        } catch (e) {
          toast.error("Could not connect. Please refresh.");
        }
      }
    })();
  }, [refresh, sessionId]);

  useEffect(() => {
    const id = setInterval(refresh, POLL_MS);
    window.addEventListener("focus", refresh);
    return () => { clearInterval(id); window.removeEventListener("focus", refresh); };
  }, [refresh]);

  const run = useCallback(async (request, tempMsg) => {
    if (tempMsg) setMessages((prev) => [...prev, tempMsg]);
    setIsLoading(true);
    try {
      const res = await request();
      setMessages((prev) => {
        const base = prev.filter((m) => m.id !== tempMsg?.id);
        return mergeById(base, [res.data.user_message, ...(res.data.messages || [])]);
      });
    } catch (e) {
      toast.error("Message could not be sent. Please try again.");
      if (tempMsg) setMessages((prev) => prev.filter((m) => m.id !== tempMsg.id));
    } finally {
      setIsLoading(false);
    }
  }, []);

  const temp = (text, type = "text") => ({
    id: `temp-${Date.now()}`, direction: "in", type, text, created_at: new Date().toISOString(),
  });

  const sendMessage = useCallback((text) => {
    if (!text?.trim()) return;
    run(() => axios.post(`${API}/assistant/send`, { session_id: sessionId, text }, { withCredentials: true }), temp(text));
  }, [run, sessionId]);

  const sendButton = useCallback((button) => {
    run(() => axios.post(`${API}/assistant/send`, { session_id: sessionId, button_id: button.id, text: button.title },
      { withCredentials: true }), temp(button.title, "button"));
  }, [run, sessionId]);

  const sendLocation = useCallback((lat, lng) => {
    run(() => axios.post(`${API}/assistant/location`, { session_id: sessionId, latitude: lat, longitude: lng },
      { withCredentials: true }), temp("📍 Location shared", "location"));
  }, [run, sessionId]);

  const upload = useCallback((path, field, blob, name, tempMsg, extra = {}) => {
    const form = new FormData();
    form.append("session_id", sessionId);
    form.append(field, blob, name);
    Object.entries(extra).forEach(([k, v]) => form.append(k, v));
    run(() => axios.post(`${API}/assistant/${path}`, form, { withCredentials: true }), tempMsg);
  }, [run, sessionId]);

  const sendVoice = useCallback((blob) => upload("voice", "audio", blob, "voice.webm", temp("🎤 Voice message", "voice")),
    [upload]);
  const sendImage = useCallback((file, caption) => upload("image", "image", file, file.name || "photo.jpg",
    temp(caption || "📷 Photo", "image"), { caption: caption || "" }), [upload]);

  return { sessionId, messages, isLoading, sendMessage, sendButton, sendLocation, sendVoice, sendImage };
}
